import { resolveServiceCategoryNameFromLabel } from "./templateToServiceCategory";

/**
 * Canonical RFI service / foundation scope names from the product catalog.
 * Used to label unknown values as "Custom" on document cards.
 *
 * Names here must match the Airtable VQQ Templates "Service Categories" value
 * exactly. Airtable renders a "New" tag next to recently added rows, and a
 * previous paste of this list glued that tag onto the name, which then rendered
 * on badges verbatim. Do not paste from the Airtable grid view: copy the field
 * value, or take the name from the catalog API response.
 */
export const RFI_CATALOG_SERVICE_NAMES = new Set([
  "Foundation - All",
  "Foundation - Business",
  "Foundation - Quality",
  "Foundation - Technology",
  "Data Management",
  "Data Hosting",
  "Decentralized Trials",
  "Feasibility & Site Selection",
  "CRO Oversight",
  "Data Protection & Privacy",
  "Investigator Site Budgets",
  "Medical Writing",
  "Project Management",
  "Site Monitoring - CRA",
  "Statistical Programming & Biostatistics",
  "Investigational & Medicinal Product Management",
  "General Clinical IMP",
  "Clinical IMP Management",
  "Clinical Supply Logistics",
  "Distribution & Warehouse Services",
  "Testing & Release (incl. QP)",
  "Comparator Sourcing",
  "Compounding",
  "Controlled Materials",
  "DS/DP Manufacturing",
  "Packaging & Labeling",
  "QP Release Services (EU)",
  "Radiolabeled Materials",
  "Temperature Controlled Couriers",
  "Bioanalytical",
  "Biomarker",
  "Central Lab",
  "Microbiology",
  "ECG",
  "EEG",
  "General Analytical (Release & Stability)",
  "Imaging",
  "Sample Logistics & Biobanking",
  "Specialty (incl. DMPK)",
  "Regulatory Affairs",
  "HA Liaison Services",
  "IND/CTA Submissions",
  "IRB/IEC",
  "Medical Monitoring",
  "Medical Review & PA Qualification",
  "Pharmacovigilance",
  "Signal Detection",
  "Real World Data/Evidence",
  "Safety Boards & Committees",
  "Scientific Advisory",
  "Investigator Site",
  "Investigator Site File",
  "Patient Recruitment & Feasibility",
  "Recruitment & Retention",
  "eHealth Records for Patient Recruitment & Feasibility",
  "Patient Identification & Support Programs",
  "Patient Remote Visits",
  "Site Management - Site Networks",
  "Telemedicine",
  "Translation Services",
  "CTMS",
  "Digital Advertising",
  "eCOA/ePRO",
  "Electronic Data Capture",
  "eHealth Records to EDC Connector Apps",
  "Informed Consent",
  "Retention & Archival",
  "RTSM",
  "Trial Master File",
  "Wearables (All Types)",
]);

/** Match audit/VQA styling when no catalog color is found */
export const DEFAULT_RFI_BADGE_STYLE = {
  bgColor: "#EEF2FF",
  textColor: "#4338CA",
  borderColor: "#4338CA",
};

export const DEFAULT_GRAY_RFI_BADGE_STYLE = {
  bgColor: "#F3F4F6",
  textColor: "#6B7280",
  borderColor: "#9CA3AF",
};

function normalizeForCatalogLookup(name) {
  if (!name || typeof name !== "string") return "";
  const trimmed = name.trim();
  if (!trimmed) return "";
  return resolveServiceCategoryNameFromLabel(trimmed) || trimmed;
}

/**
 * Catalog lookup, tolerant of a value that still carries Airtable's glued "New"
 * tag. Stripping is applied to the incoming value only: the returned name always
 * comes from the catalog set above, so a contaminated value can never reach a
 * badge. Never match by gluing "New" onto the candidate, that only reintroduces
 * the tag as a display name.
 */
function findCatalogName(candidate) {
  const n = normalizeForCatalogLookup(candidate);
  if (!n) return null;

  for (const variant of [n, n.replace(/New$/i, "").trim()]) {
    if (!variant) continue;
    if (RFI_CATALOG_SERVICE_NAMES.has(variant)) return variant;
    const lower = variant.toLowerCase();
    for (const k of RFI_CATALOG_SERVICE_NAMES) {
      if (k.toLowerCase() === lower) return k;
    }
  }

  return null;
}

/**
 * True if the string matches the catalog (exact, case-insensitive, or API variant without glued "New").
 */
export function isKnownRfiCatalogServiceName(rawName) {
  return findCatalogName(rawName) !== null;
}

/**
 * Badge label for RFI scope: the catalog name when the value matches the official list,
 * otherwise the custom template's own name. Collapsing customs to a literal "Custom" hid
 * which template a VQQ was for; the Type column already distinguishes Diligent from Custom.
 * Blank input falls back to "Custom". Legacy "Core Capabilities" maps to
 * "Foundation - All" via templateToServiceCategory.
 */
export function getRfiCapabilityBadgeDisplayName(rawName) {
  const n = normalizeForCatalogLookup(rawName);
  if (!n) return "Custom";
  // The raw name, not the lookup candidate: normalization strips matching-only tokens
  // (Diligent prefix, RFI/VQQ suffixes) that belong in a custom name's display.
  return findCatalogName(n) ?? String(rawName).trim();
}
